/**
 * Ledger-driven tier recommendations.
 *
 * Turns the routing ledger's per-model outcome measurements into concrete
 * suggestions for the smart-routing tier pins: switch a pin to a measured
 * model that performs meaningfully better, pin a tier that has no pin yet, or
 * confirm the current pin is holding up. Pure functions; no I/O.
 */

import { MIN_RELIABILITY_SAMPLE, type ModelOutcomeStats } from "./routing-ledger.js";
import { type ComplexityTier, tierForOutputCost } from "./smart-router.js";

export interface ModelCostInfo {
	/** `provider/modelId` */
	key: string;
	outputCostPerMillion: number;
}

export interface TierRecommendation {
	tier: ComplexityTier;
	/** Pinned key, if any. */
	current?: string;
	/** Recommended key. */
	suggested: string;
	/** One sentence, numbers included. */
	reason: string;
	kind: "switch" | "pin" | "keep";
}

export interface TierPins {
	fast?: string;
	balanced?: string;
	powerful?: string;
}

const TIERS: readonly ComplexityTier[] = ["fast", "balanced", "powerful"];

/** A candidate must beat the pin by this much success rate to justify a switch. */
const SUCCESS_MARGIN = 0.05;
/** Success rates within this band count as equal. */
const SUCCESS_EQUAL_BAND = 0.02;
/** With equal success, a candidate must cost less than this fraction per turn. */
const COST_RATIO_THRESHOLD = 0.6;

interface Measured {
	key: string;
	stats: ModelOutcomeStats;
	costPerTurn: number;
	editFailRate: number;
}

function measure(key: string, stats: ModelOutcomeStats): Measured {
	return {
		key,
		stats,
		costPerTurn: stats.turns > 0 ? stats.totalCost / stats.turns : 0,
		editFailRate: stats.editsAttempted > 0 ? stats.editsFailed / stats.editsAttempted : 0,
	};
}

/** Higher success first, then fewer edit failures, then cheaper per turn. */
function compareMeasured(a: Measured, b: Measured): number {
	if (a.stats.successRate !== b.stats.successRate) return b.stats.successRate - a.stats.successRate;
	if (a.editFailRate !== b.editFailRate) return a.editFailRate - b.editFailRate;
	return a.costPerTurn - b.costPerTurn;
}

function isMeaningfullyBetter(candidate: Measured, baseline: Measured): boolean {
	const successDelta = candidate.stats.successRate - baseline.stats.successRate;
	if (successDelta >= SUCCESS_MARGIN) return true;
	if (Math.abs(successDelta) <= SUCCESS_EQUAL_BAND) {
		return candidate.costPerTurn < baseline.costPerTurn * COST_RATIO_THRESHOLD;
	}
	return false;
}

function formatPercent(rate: number): string {
	return `${Math.round(rate * 100)}%`;
}

function formatCostPerTurn(cost: number): string {
	if (cost > 0 && cost < 0.0005) return `$${cost.toFixed(4)}/turn`;
	return `$${cost.toFixed(3)}/turn`;
}

/** Full description: key, success, turns, cost per turn. */
function describeFull(m: Measured): string {
	const turns = `${m.stats.turns} turn${m.stats.turns === 1 ? "" : "s"}`;
	return `${m.key}: ${formatPercent(m.stats.successRate)} success over ${turns} at ${formatCostPerTurn(m.costPerTurn)}`;
}

/** Short description for the comparison side: key, success, cost per turn. */
function describeShort(m: Measured): string {
	return `${m.key}: ${formatPercent(m.stats.successRate)} at ${formatCostPerTurn(m.costPerTurn)}`;
}

/** Build a `costOf` lookup from registry-derived cost info. */
export function createCostLookup(models: readonly ModelCostInfo[]): (key: string) => number | undefined {
	const costs = new Map<string, number>();
	for (const m of models) costs.set(m.key, m.outputCostPerMillion);
	return (key) => costs.get(key);
}

/** Map registry models to `ModelCostInfo` keyed by `provider/id`. */
export function toModelCostInfo(
	models: ReadonlyArray<{ provider: string; id: string; cost: { output: number } }>,
): ModelCostInfo[] {
	return models.map((m) => ({ key: `${m.provider}/${m.id}`, outputCostPerMillion: m.cost.output }));
}

export function recommendTierModels(
	stats: ReadonlyMap<string, ModelOutcomeStats>,
	pins: TierPins,
	costOf: (key: string) => number | undefined,
	options?: { minTurns?: number },
): TierRecommendation[] {
	const minTurns = options?.minTurns ?? MIN_RELIABILITY_SAMPLE;

	const byTier = new Map<ComplexityTier, Measured[]>();
	for (const tier of TIERS) byTier.set(tier, []);
	for (const [key, s] of stats) {
		if (s.turns < minTurns) continue;
		const cost = costOf(key);
		if (cost === undefined) continue;
		byTier.get(tierForOutputCost(cost))?.push(measure(key, s));
	}
	for (const list of byTier.values()) list.sort(compareMeasured);

	const recs: TierRecommendation[] = [];
	for (const tier of TIERS) {
		const candidates = byTier.get(tier) ?? [];
		const pin = pins[tier];

		if (pin) {
			const pinnedStats = stats.get(pin);
			if (!pinnedStats || pinnedStats.turns < minTurns) continue;
			const pinned = measure(pin, pinnedStats);
			const best = candidates.find((c) => c.key !== pin);

			if (best && isMeaningfullyBetter(best, pinned)) {
				recs.push({
					tier,
					current: pin,
					suggested: best.key,
					kind: "switch",
					reason: `${describeFull(best)} vs ${describeShort(pinned)}`,
				});
			} else {
				const alternative = best
					? `best alternative ${describeShort(best)} is not meaningfully better`
					: "no other measured model in this tier";
				recs.push({
					tier,
					current: pin,
					suggested: pin,
					kind: "keep",
					reason: `${describeFull(pinned)}; ${alternative}`,
				});
			}
			continue;
		}

		if (candidates.length === 0) continue;
		const best = candidates[0];
		const enoughData = candidates.length >= 2 || best.stats.turns >= 2 * minTurns;
		if (!enoughData) continue;
		const runnerUp = candidates[1];
		recs.push({
			tier,
			suggested: best.key,
			kind: "pin",
			reason: runnerUp ? `${describeFull(best)} (next: ${describeShort(runnerUp)})` : describeFull(best),
		});
	}
	return recs;
}

export function formatRecommendations(recs: readonly TierRecommendation[]): string {
	if (recs.length === 0) {
		return "No tier suggestions yet - more measured turns are needed before the ledger can compare models.";
	}
	const lines: string[] = ["Tier suggestions (from your local ledger, last 30 days):"];
	for (const rec of recs) {
		switch (rec.kind) {
			case "switch":
				lines.push(`- ${rec.tier}: switch ${rec.current} → ${rec.suggested} — ${rec.reason}`);
				break;
			case "pin":
				lines.push(`- ${rec.tier}: pin ${rec.suggested} — ${rec.reason}`);
				break;
			case "keep":
				lines.push(`- ${rec.tier}: keep ${rec.current} — ${rec.reason}`);
				break;
		}
	}
	lines.push("");
	lines.push("Apply with /settings → Smart routing, or set smartRoutingModels in settings.json.");
	return lines.join("\n");
}
